import React, { useRef } from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";

const topRowPosts = [
  {
    name: "Sara H.",
    city: "Jeddah",
    initials: "SH",
    color: "#FF9B4A",
    text: "Got legal guidance on my rental contract through aroundU. Clear, fast and free advice.",
    likes: 212,
    replies: 33,
  },
  {
    name: "Abdullah R.",
    city: "Dammam",
    initials: "AR",
    color: "#9272F8",
    text: "Needed a parcel delivered same-day across the city. Sorted within the hour.",
    likes: 176,
    replies: 41,
  },
  {
    name: "Layla A.",
    city: "Mecca",
    initials: "LA",
    color: "#FA5F91",
    text: "The community helped me find a trusted plumber nearby. No more guessing.",
    likes: 151,
    replies: 28,
  },
  {
    name: "Faisal T.",
    city: "Riyadh",
    initials: "FT",
    color: "#35C276",
    text: "Banking question answered by neighbours instead of waiting on hold all morning.",
    likes: 88,
    replies: 19,
  },
];

const bottomRowPosts = [
  {
    name: "Noura S.",
    city: "Khobar",
    initials: "NS",
    color: "#00A99D",
    text: "Found towing support after midnight. Someone nearby shared the right contact instantly.",
    likes: 124,
    replies: 22,
  },
  {
    name: "Maha K.",
    city: "Medina",
    initials: "MK",
    color: "#D4B06A",
    text: "Asked where to renew paperwork and got step-by-step help from people who had done it.",
    likes: 197,
    replies: 37,
  },
  {
    name: "Omar B.",
    city: "Tabuk",
    initials: "OB",
    color: "#4B9CFF",
    text: "Shared a delivery recommendation and three neighbours confirmed it was reliable.",
    likes: 142,
    replies: 25,
  },
  {
    name: "Hind M.",
    city: "Taif",
    initials: "HM",
    color: "#EF6C73",
    text: "A simple post helped me compare service providers before booking. Super useful.",
    likes: 103,
    replies: 18,
  },
];

const CommunityCard = ({ post }) => (
  <Box
    sx={{
      flex: "0 0 auto",
      width: { xs: 280, sm: 330, md: 372 },
      height: { xs: 122, md: 132 },
      p: { xs: 1.55, md: 1.75 },
      borderRadius: { xs: 2.5, md: 3 },
      backgroundColor: "#FFFFFF",
      border: "1px solid rgba(9, 34, 31, 0.08)",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: { xs: 36, md: 40 },
          height: { xs: 36, md: 40 },
          flex: "0 0 auto",
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          color: "#FFFFFF",
          fontSize: { xs: "0.8rem", md: "0.86rem" },
          fontWeight: 850,
          lineHeight: 1,
          backgroundColor: post.color,
        }}
      >
        {post.initials}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: "#10211E",
            fontSize: { xs: "0.82rem", md: "0.9rem" },
            fontWeight: 850,
            lineHeight: 1.2,
            letterSpacing: 0,
          }}
        >
          {post.name}
        </Typography>
        <Stack direction="row" spacing={0.35} alignItems="center" sx={{ mt: 0.25 }}>
          <LocationOnRoundedIcon sx={{ color: "#EF4D4D", fontSize: 12 }} />
          <Typography
            sx={{
              color: "rgba(16, 33, 30, 0.58)",
              fontSize: { xs: "0.68rem", md: "0.74rem" },
              fontWeight: 650,
              lineHeight: 1.2,
              letterSpacing: 0,
            }}
          >
            {post.city} · aroundU
          </Typography>
        </Stack>
      </Box>
    </Stack>

    <Typography
      sx={{
        mt: { xs: 1.05, md: 1.15 },
        color: "rgba(16, 33, 30, 0.75)",
        display: "-webkit-box",
        overflow: "hidden",
        fontSize: { xs: "0.76rem", md: "0.84rem" },
        lineHeight: 1.38,
        letterSpacing: 0,
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
      }}
    >
      {post.text}
    </Typography>

    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: { xs: 1.1, md: 1.25 } }}>
      <Stack direction="row" spacing={0.55} alignItems="center">
        <FavoriteRoundedIcon sx={{ color: "#16A394", fontSize: 15 }} />
        <Typography sx={{ color: "rgba(16, 33, 30, 0.62)", fontWeight: 750, fontSize: "0.74rem" }}>
          {post.likes}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.55} alignItems="center">
        <ChatBubbleOutlineRoundedIcon sx={{ color: "#16A394", fontSize: 16 }} />
        <Typography sx={{ color: "rgba(16, 33, 30, 0.62)", fontWeight: 750, fontSize: "0.74rem" }}>
          {post.replies}
        </Typography>
      </Stack>
    </Stack>
  </Box>
);

const MarqueeRow = ({ posts, x }) => {
  return (
    <Box
      sx={{
        overflow: "hidden",
        width: "100%",
        maskImage:
          "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)",
        WebkitMaskImage:
          "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)",
      }}
    >
      <Box
        component={motion.div}
        style={{ x }}
        sx={{ width: "max-content", display: "flex", py: 1 }}
      >
        {[0, 1].map((groupIndex) => (
          <Stack
            key={`group-${groupIndex}`}
            direction="row"
            spacing={{ xs: 2, md: 3 }}
            sx={{ pr: { xs: 2, md: 3 } }}
          >
            {posts.map((post) => (
              <CommunityCard key={`${groupIndex}-${post.name}`} post={post} />
            ))}
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

const CommunitySection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // Tie scroll progress through the section to gentle parallax offsets.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const patternY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const blobOneY = useTransform(scrollYProgress, [0, 1], ["-16%", "16%"]);
  const blobTwoY = useTransform(scrollYProgress, [0, 1], ["14%", "-14%"]);

  // Rows slide horizontally as the page scrolls through the section (opposite ways).
  const topRowX = useTransform(
    scrollYProgress,
    [0, 1],
    prefersReducedMotion ? ["-25%", "-25%"] : ["-8%", "-42%"]
  );
  const bottomRowX = useTransform(
    scrollYProgress,
    [0, 1],
    prefersReducedMotion ? ["-25%", "-25%"] : ["-42%", "-8%"]
  );

  return (
    <Box
      component="section"
      ref={sectionRef}
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 6, md: 7.5 },
        background:
          "radial-gradient(circle at 50% 0%, rgba(0, 213, 200, 0.1), transparent 34%), #F4F8F6",
      }}
    >
      {/* Parallax background layers */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <Box
          component={motion.div}
          style={prefersReducedMotion ? undefined : { y: blobOneY }}
          sx={{
            position: "absolute",
            top: "-10%",
            left: "-6%",
            width: { xs: 260, md: 420 },
            height: { xs: 260, md: 420 },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(0, 204, 187, 0.22), transparent 68%)",
            filter: "blur(8px)",
          }}
        />
        <Box
          component={motion.div}
          style={prefersReducedMotion ? undefined : { y: blobTwoY }}
          sx={{
            position: "absolute",
            bottom: "-12%",
            right: "-8%",
            width: { xs: 280, md: 460 },
            height: { xs: 280, md: 460 },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(146, 114, 248, 0.16), transparent 68%)",
            filter: "blur(8px)",
          }}
        />
        <Box
          component={motion.div}
          style={prefersReducedMotion ? undefined : { y: patternY }}
          sx={{
            position: "absolute",
            top: "-12%",
            left: 0,
            right: 0,
            height: "124%",
            backgroundImage:
              "radial-gradient(circle, rgba(9, 153, 143, 0.13) 1.4px, transparent 1.6px)",
            backgroundSize: "26px 26px",
            maskImage:
              "radial-gradient(ellipse 80% 70% at 50% 40%, #000 55%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 80% 70% at 50% 40%, #000 55%, transparent 100%)",
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Stack
          component={motion.div}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          spacing={{ xs: 1.55, md: 1.85 }}
          alignItems="center"
          sx={{ textAlign: "center", mx: "auto", maxWidth: 880 }}
        >
          <Typography
            sx={{
              color: "#09998F",
              fontSize: { xs: "0.76rem", md: "0.82rem" },
              fontWeight: 850,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
            }}
          >
            {t("community_eyebrow")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              color: "#10211E",
              fontSize: { xs: "2rem", sm: "2.75rem", md: "3.55rem" },
              fontWeight: 900,
              lineHeight: { xs: 1.04, md: 1 },
              letterSpacing: 0,
              maxWidth: 690,
            }}
          >
            {t("community_title")}
          </Typography>
          <Typography
            sx={{
              color: "rgba(16, 33, 30, 0.62)",
              fontSize: { xs: "0.95rem", md: "1.08rem" },
              lineHeight: 1.6,
              maxWidth: 620,
              letterSpacing: 0,
            }}
          >
            {t("community_subtitle")}
          </Typography>
          <Button
            component="a"
            href="https://community.aroundu.app"
            endIcon={<ArrowOutwardRoundedIcon />}
            sx={{
              mt: 0.6,
              px: { xs: 2.35, md: 2.8 },
              py: { xs: 1.05, md: 1.18 },
              minHeight: 44,
              borderRadius: 999,
              color: "#FFFFFF",
              backgroundColor: "#00ccbb",
              fontSize: { xs: "0.9rem", md: "0.95rem" },
              fontWeight: 800,
              letterSpacing: 0,
              textTransform: "none",
              boxShadow: "0 16px 36px rgba(7, 20, 26, 0.16)",
              "&:hover": {
                backgroundColor: "#00b8a8",
                boxShadow: "0 18px 40px rgba(0, 204, 187, 0.28)",
              },
              "&:focus-visible": {
                outline: "3px solid rgba(0, 213, 200, 0.45)",
                outlineOffset: "4px",
              },
            }}
          >
            {t("visit_community")}
          </Button>
        </Stack>
      </Container>

      <Stack
        dir="ltr"
        spacing={{ xs: 0.8, md: 1 }}
        sx={{ mt: { xs: 4.2, md: 5 }, position: "relative", zIndex: 1 }}
      >
        <MarqueeRow posts={topRowPosts} x={topRowX} />
        <MarqueeRow posts={bottomRowPosts} x={bottomRowX} />
      </Stack>
    </Box>
  );
};

export default CommunitySection;
